import {
  Title,
  Text,
  Stack,
  Paper,
  Card,
  Group,
  Grid,
  Badge,
  ThemeIcon,
  Progress,
  Skeleton,
  Alert,
  RingProgress,
  SimpleGrid,
} from '@mantine/core';
import {
  IconDeviceDesktop,
  IconAlertTriangle,
  IconShieldX,
  IconShieldCheck,
  IconCloud,
  IconCloudOff,
  IconRefresh,
  IconCheck,
  IconX,
  IconClock,
  IconActivity,
  IconServer,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { useDashboard } from '@/hooks';

export function DashboardPage() {
  const navigate = useNavigate();
  const { data: stats, isLoading, isError } = useDashboard();

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Dashboard</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height={140} radius="md" />
          ))}
        </SimpleGrid>
        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Skeleton height={300} radius="md" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Skeleton height={300} radius="md" />
          </Grid.Col>
        </Grid>
      </Stack>
    );
  }

  if (isError || !stats) {
    return (
      <Stack gap="lg">
        <Title order={2}>Dashboard</Title>
        <Alert color="red" icon={<IconX size={16} />} title="Error loading dashboard">
          Failed to load dashboard data. Please try again.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Dashboard</Title>
          <Text c="dimmed" size="sm">
            Fleet overview and system status
          </Text>
        </div>
      </Group>

      {/* Top Stats Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        {/* Devices Card */}
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/devices')}
        >
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed" fw={500}>
              Devices
            </Text>
            <ThemeIcon variant="light" size="lg" color="blue">
              <IconDeviceDesktop size={20} />
            </ThemeIcon>
          </Group>
          <Text size="xl" fw={700}>
            {stats.devices.total}
          </Text>
          <Group gap="xs" mt="sm">
            <Badge color="green" variant="light" size="sm">
              {stats.devices.online} online
            </Badge>
            {stats.devices.stale > 0 && (
              <Badge color="orange" variant="light" size="sm">
                {stats.devices.stale} stale
              </Badge>
            )}
          </Group>
        </Card>

        {/* Drift Card */}
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/devices?driftSeverity=high')}
        >
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed" fw={500}>
              Drift Alerts
            </Text>
            <ThemeIcon
              variant="light"
              size="lg"
              color={stats.drift.hasIssues ? 'red' : 'green'}
            >
              <IconAlertTriangle size={20} />
            </ThemeIcon>
          </Group>
          <Text size="xl" fw={700} c={stats.drift.high > 0 ? 'red' : undefined}>
            {stats.drift.high + stats.drift.medium}
          </Text>
          <Group gap="xs" mt="sm">
            {stats.drift.high > 0 && (
              <Badge color="red" variant="filled" size="sm">
                {stats.drift.high} high
              </Badge>
            )}
            {stats.drift.medium > 0 && (
              <Badge color="yellow" variant="filled" size="sm">
                {stats.drift.medium} medium
              </Badge>
            )}
            {!stats.drift.hasIssues && (
              <Badge color="green" variant="light" size="sm">
                All clear
              </Badge>
            )}
          </Group>
        </Card>

        {/* Dangerous Software Card */}
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/devices')}
        >
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed" fw={500}>
              Dangerous Software
            </Text>
            <ThemeIcon
              variant="light"
              size="lg"
              color={stats.software.hasIssues ? 'red' : 'green'}
            >
              {stats.software.hasIssues ? (
                <IconShieldX size={20} />
              ) : (
                <IconShieldCheck size={20} />
              )}
            </ThemeIcon>
          </Group>
          <Text size="xl" fw={700} c={stats.software.dangerous > 0 ? 'red' : undefined}>
            {stats.software.dangerous}
          </Text>
          <Text size="xs" c="dimmed" mt="sm">
            {stats.software.totalUnique} unique software items
          </Text>
        </Card>

        {/* FOG Status Card */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed" fw={500}>
              FOG Server
            </Text>
            <ThemeIcon
              variant="light"
              size="lg"
              color={stats.fog.connected ? 'green' : 'red'}
            >
              {stats.fog.connected ? (
                <IconCloud size={20} />
              ) : (
                <IconCloudOff size={20} />
              )}
            </ThemeIcon>
          </Group>
          <Text size="xl" fw={700}>
            {stats.fog.connected ? 'Connected' : 'Offline'}
          </Text>
          {stats.fog.connected && (
            <Group gap="xs" mt="sm">
              <Badge color="blue" variant="light" size="sm">
                {stats.fog.hostCount} hosts
              </Badge>
              {stats.fog.activeTasks > 0 && (
                <Badge color="cyan" variant="light" size="sm">
                  {stats.fog.activeTasks} active tasks
                </Badge>
              )}
            </Group>
          )}
        </Card>
      </SimpleGrid>

      {/* Main Content */}
      <Grid>
        {/* Left Column - Software & Drift Details */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            {/* Top Dangerous Software */}
            <Paper p="lg" withBorder radius="md">
              <Group justify="space-between" mb="md">
                <div>
                  <Text fw={600}>Top Dangerous Software</Text>
                  <Text size="sm" c="dimmed">
                    Most widespread prohibited software
                  </Text>
                </div>
                <Badge color={stats.software.dangerous > 0 ? 'red' : 'green'}>
                  {stats.software.dangerous} types found
                </Badge>
              </Group>

              {stats.software.topDangerous.length > 0 ? (
                <Stack gap="sm">
                  {stats.software.topDangerous.map((sw, index) => (
                    <Paper key={index} p="sm" bg="red.0" radius="sm">
                      <Group justify="space-between">
                        <div>
                          <Group gap="xs">
                            <IconShieldX size={16} color="var(--mantine-color-red-6)" />
                            <Text size="sm" fw={500}>
                              {sw.name}
                            </Text>
                          </Group>
                          {sw.publisher && (
                            <Text size="xs" c="dimmed" ml={24}>
                              {sw.publisher}
                            </Text>
                          )}
                        </div>
                        <Group gap="xs">
                          <Badge
                            size="sm"
                            color={
                              sw.severity === 'critical'
                                ? 'red'
                                : sw.severity === 'high'
                                ? 'orange'
                                : 'yellow'
                            }
                          >
                            {sw.severity}
                          </Badge>
                          <Badge size="sm" variant="light">
                            {sw.deviceCount} device{sw.deviceCount > 1 ? 's' : ''}
                          </Badge>
                        </Group>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Paper p="xl" bg="green.0" radius="sm">
                  <Group justify="center" gap="xs">
                    <IconShieldCheck size={20} color="var(--mantine-color-green-6)" />
                    <Text c="green" fw={500}>
                      No dangerous software detected
                    </Text>
                  </Group>
                </Paper>
              )}
            </Paper>

            {/* Device Health Overview */}
            <Paper p="lg" withBorder radius="md">
              <Text fw={600} mb="md">
                Device Health
              </Text>
              <Grid>
                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm">Active Devices</Text>
                      <Text size="sm" fw={500}>
                        {stats.devices.active} / {stats.devices.total}
                      </Text>
                    </Group>
                    <Progress
                      value={(stats.devices.active / stats.devices.total) * 100}
                      color="green"
                      size="lg"
                      radius="xl"
                    />
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm">Online (24h)</Text>
                      <Text size="sm" fw={500}>
                        {stats.devices.online} / {stats.devices.total}
                      </Text>
                    </Group>
                    <Progress
                      value={(stats.devices.online / stats.devices.total) * 100}
                      color="blue"
                      size="lg"
                      radius="xl"
                    />
                  </Stack>
                </Grid.Col>
              </Grid>
            </Paper>
          </Stack>
        </Grid.Col>

        {/* Right Column - Status & Activity */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            {/* Sync Status */}
            <Paper p="lg" withBorder radius="md">
              <Group justify="space-between" mb="md">
                <Text fw={600}>OCS Sync Status</Text>
                <ThemeIcon
                  variant="light"
                  size="sm"
                  color={
                    stats.sync.isRunning
                      ? 'cyan'
                      : stats.sync.lastRunStatus === 'success'
                      ? 'green'
                      : stats.sync.lastRunStatus === 'failed'
                      ? 'red'
                      : 'gray'
                  }
                >
                  {stats.sync.isRunning ? (
                    <IconRefresh size={14} />
                  ) : stats.sync.lastRunStatus === 'success' ? (
                    <IconCheck size={14} />
                  ) : (
                    <IconClock size={14} />
                  )}
                </ThemeIcon>
              </Group>

              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Status
                  </Text>
                  <Badge
                    color={stats.sync.enabled ? 'green' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {stats.sync.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Last Run
                  </Text>
                  <Text size="sm">
                    {stats.sync.lastRunAt
                      ? formatRelativeTime(stats.sync.lastRunAt)
                      : 'Never'}
                  </Text>
                </Group>
                {stats.sync.enabled && stats.sync.nextRunAt && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Next Run
                    </Text>
                    <Text size="sm">{formatRelativeTime(stats.sync.nextRunAt)}</Text>
                  </Group>
                )}
              </Stack>
            </Paper>

            {/* Drift Summary Ring */}
            <Paper p="lg" withBorder radius="md">
              <Text fw={600} mb="md">
                Drift Summary
              </Text>
              <Group justify="center">
                <RingProgress
                  size={150}
                  thickness={16}
                  roundCaps
                  sections={[
                    {
                      value: stats.drift.total > 0
                        ? (stats.drift.high / stats.drift.total) * 100
                        : 0,
                      color: 'red',
                      tooltip: `High: ${stats.drift.high}`,
                    },
                    {
                      value: stats.drift.total > 0
                        ? (stats.drift.medium / stats.drift.total) * 100
                        : 0,
                      color: 'yellow',
                      tooltip: `Medium: ${stats.drift.medium}`,
                    },
                    {
                      value: stats.drift.total > 0
                        ? (stats.drift.low / stats.drift.total) * 100
                        : 0,
                      color: 'blue',
                      tooltip: `Low: ${stats.drift.low}`,
                    },
                  ]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {stats.drift.total}
                    </Text>
                  }
                />
              </Group>
              <Group justify="center" gap="lg" mt="md">
                <Group gap={4}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 4,
                      backgroundColor: 'var(--mantine-color-red-6)',
                    }}
                  />
                  <Text size="xs">High ({stats.drift.high})</Text>
                </Group>
                <Group gap={4}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 4,
                      backgroundColor: 'var(--mantine-color-yellow-6)',
                    }}
                  />
                  <Text size="xs">Medium ({stats.drift.medium})</Text>
                </Group>
                <Group gap={4}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 4,
                      backgroundColor: 'var(--mantine-color-blue-6)',
                    }}
                  />
                  <Text size="xs">Low ({stats.drift.low})</Text>
                </Group>
              </Group>
            </Paper>

            {/* Recent Activity */}
            <Paper p="lg" withBorder radius="md">
              <Group justify="space-between" mb="md">
                <Text fw={600}>Recent Activity</Text>
                <IconActivity size={16} color="var(--mantine-color-dimmed)" />
              </Group>
              <Stack gap="xs">
                {stats.recentActivity.recentDevices.map((device, index) => (
                  <Group key={index} justify="space-between">
                    <Group gap="xs">
                      <IconServer size={14} color="var(--mantine-color-dimmed)" />
                      <Text size="sm">{device.hostname}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {device.updatedAt ? formatRelativeTime(device.updatedAt) : '-'}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

/**
 * Format relative time from ISO string
 */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMs < 0) {
    const futureMins = Math.abs(diffMins);
    const futureHours = Math.floor(futureMins / 60);
    if (futureHours > 0) return `in ${futureHours}h`;
    return `in ${futureMins}m`;
  }

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return 'Just now';
}
